import React, { ReactNode } from "react";
import { Modal } from "./ui/modal";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from "./ui/table";
import { Item, LOST, RETURN, STEPS } from "../pages/AddActivity";
import { Badge } from "./ui/badge";
import { getQuantityColumnName } from "../lib/utils";

interface PreviewMovementModalProps {
  isPreviewOpen: boolean;
  setIsPreviewOpen: (isPreviewOpen: boolean) => void;
  setCurrentStep: (currentStep: string) => void;
  handleEditActivityTypeLocationAndDate: () => void;
  activityType: string;
  activitySubType: string;
  returnType?: string;
  source: string;
  destination: string;
  plannedDate: Date;
  notes: string;
  items: Item[];
}

const PreviewMovementModal: React.FC<PreviewMovementModalProps> = ({
  isPreviewOpen,
  setIsPreviewOpen,
  setCurrentStep,
  handleEditActivityTypeLocationAndDate,
  activityType,
  activitySubType,
  returnType,
  source,
  destination,
  plannedDate,
  notes,
  items,
}) => {
  return (
    <Modal
      isOpen={isPreviewOpen}
      onClose={() => setIsPreviewOpen(false)}
      title="Movement Activity Preview"
      description="Preview details that movement activity details contain"
      className="w-full max-w-5xl"
    >
      <div className="px-6 py-4 space-y-8">
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Movement Type</h3>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                handleEditActivityTypeLocationAndDate();
                setIsPreviewOpen(false);
              }}
              className="h-8 px-3 text-sm text-primary-600 hover:text-primary-700 hover:bg-transparent"
            >
              Edit
            </Button>
          </div>
          <p className="text-sm text-gray-700">
            {activityType} - {activitySubType}
            {activitySubType === RETURN && returnType
              ? ` (${returnType === "Full" ? "Full return" : "Partial return"})`
              : ""}
          </p>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Location Details</h3>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                handleEditActivityTypeLocationAndDate();
                setIsPreviewOpen(false);
              }}
              className="h-9 px-3 text-sm text-primary-600 hover:bg-primary-50"
            >
              Edit
            </Button>
          </div>
          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              <span className="text-gray-500">Source:</span> {source}
            </p>
            {activitySubType !== LOST && (
              <p className="text-sm text-gray-700">
                <span className="text-gray-500">Destination:</span> {destination}
              </p>
            )}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Planned Date</h3>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                handleEditActivityTypeLocationAndDate();
                setIsPreviewOpen(false);
              }}
              className="h-8 px-3 text-sm text-primary-600 hover:text-primary-700 hover:bg-transparent"
            >
              Edit
            </Button>
          </div>
          <p className="text-sm text-gray-700">
            {plannedDate
              ? plannedDate.toLocaleDateString()
              : "No date selected"}
          </p>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Notes</h3>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCurrentStep(STEPS[1].id);
                setIsPreviewOpen(false);
              }}
              className="h-8 px-3 text-sm text-primary-600 hover:text-primary-700 hover:bg-transparent"
            >
              Edit
            </Button>
          </div>
          <p className="text-sm text-gray-700">{notes}</p>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Items</h3>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCurrentStep(STEPS[1].id);
                setIsPreviewOpen(false);
              }}
              className="h-9 px-3 text-sm text-primary-600 hover:bg-primary-50"
            >
              Edit
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableColumnHeader>S/No</TableColumnHeader>
                <TableColumnHeader>
                  Description/Length/Weight(KG)
                </TableColumnHeader>
                <TableColumnHeader>Available Qty</TableColumnHeader>
                <TableColumnHeader>
                  {getQuantityColumnName(activitySubType)}
                </TableColumnHeader>
                {activitySubType === RETURN && (
                  <TableColumnHeader>Damaged Qty</TableColumnHeader>
                )}
                {activitySubType === RETURN && (
                  <TableColumnHeader>Total</TableColumnHeader>
                )}
                {activitySubType === RETURN && (
                  <TableColumnHeader>Reason</TableColumnHeader>
                )}
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {items?.map((item, index) => {
                return (
                  <TableRow key={index} className="border">
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <p>{item.description}</p>
                      <p className="text-xs text-gray-700">
                        {[item.length, item.unit_weight]
                          .filter(Boolean)
                          .join(" • ")}
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="onhold">{item.quantity}</Badge>
                      <p className="text-xs text-gray-700">
                        {Math.floor(item.unit_weight * (item?.quantity ?? 0))}{" "}
                        KG
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="open">{item.rental_quantity}</Badge>
                      <p className="text-xs text-gray-700">
                        {Math.floor(
                          item.unit_weight * (item?.rental_quantity ?? 0),
                        )}{" "}
                        KG
                      </p>
                    </TableCell>
                    {activitySubType === RETURN && (
                      <TableCell>
                        <Badge variant="closed">
                          {item.damaged_quantity ?? 0}
                        </Badge>
                        <p className="text-xs text-gray-700">
                          {Math.floor(
                            item.unit_weight * (item?.damaged_quantity ?? 0),
                          )}{" "}
                          KG
                        </p>
                      </TableCell>
                    )}
                    {activitySubType === RETURN && (
                      <TableCell>
                        <Badge variant="onhold">
                          {(item.rental_quantity ?? 0) +
                            (item.damaged_quantity ?? 0)}
                        </Badge>
                        <p className="text-xs text-gray-700">
                          {Math.floor(
                            item.unit_weight *
                              ((item.rental_quantity ?? 0) +
                                (item.damaged_quantity ?? 0)),
                          )}{" "}
                          KG
                        </p>
                      </TableCell>
                    )}
                    {activitySubType === RETURN && (
                      <TableCell>
                        <p className="text-sm text-gray-700">
                          {item.reason?.trim() ? item.reason : "—"}
                        </p>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              <TableRow className="border">
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <Badge variant="onhold">
                    {items && items.length > 0
                      ? items.reduce(
                          (total: number, item: Item) =>
                            total + (item?.quantity ?? 0),
                          0,
                        )
                      : 0}
                  </Badge>
                  <p className="text-xs text-gray-700">
                    {items && items.length > 0
                      ? items.reduce(
                          (total: number, item: Item) =>
                            total +
                            Math.floor(
                              item.unit_weight * (item?.quantity ?? 0),
                            ),
                          0,
                        )
                      : 0}{" "}
                    KG
                  </p>
                </TableCell>
                <TableCell>
                  <Badge variant="open">
                    {items && items.length > 0
                      ? items.reduce(
                          (total: number, item: Item) =>
                            total + (item?.rental_quantity ?? 0),
                          0,
                        )
                      : 0}
                  </Badge>
                  <p className="text-xs text-gray-700">
                    {items && items.length > 0
                      ? items.reduce(
                          (total: number, item: Item) =>
                            total +
                            Math.floor(
                              item.unit_weight * (item?.rental_quantity ?? 0),
                            ),
                          0,
                        )
                      : 0}{" "}
                    KG
                  </p>
                </TableCell>
                {activitySubType === RETURN && (
                  <TableCell>
                    <Badge variant="closed">
                      {items && items.length > 0
                        ? items.reduce(
                            (total: number, item: Item) =>
                              total + (item?.damaged_quantity ?? 0),
                            0,
                          )
                        : 0}
                    </Badge>
                    <p className="text-xs text-gray-700">
                      {items && items.length > 0
                        ? items.reduce(
                            (total: number, item: Item) =>
                              total +
                              Math.floor(
                                item.unit_weight *
                                  (item?.damaged_quantity ?? 0),
                              ),
                            0,
                          )
                        : 0}{" "}
                      KG
                    </p>
                  </TableCell>
                )}
                {activitySubType === RETURN && (
                  <TableCell>
                    <Badge variant="onhold">
                      {items && items.length > 0
                        ? items.reduce(
                            (total: number, item: Item) =>
                              total +
                              (item?.rental_quantity ?? 0) +
                              (item?.damaged_quantity ?? 0),
                            0,
                          )
                        : 0}
                    </Badge>
                    <p className="text-xs text-gray-700">
                      {items && items.length > 0
                        ? items.reduce(
                            (total: number, item: Item) =>
                              total +
                              Math.floor(
                                item.unit_weight *
                                  ((item?.rental_quantity ?? 0) +
                                    (item?.damaged_quantity ?? 0)),
                              ),
                            0,
                          )
                        : 0}{" "}
                      KG
                    </p>
                  </TableCell>
                )}
                {activitySubType === RETURN && <TableCell></TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </section>
      </div>
    </Modal>
  );
};

export default PreviewMovementModal;
